import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import partnersLogo from "@/assets/partners_logo.png";

interface ForgotPasswordFormData {
  email: string;
}

export const ForgotPasswordPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { resetPassword } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    try {
      await resetPassword(data.email);
      setEmailSent(true);
      toast.success("Email de recuperação enviado!");
    } catch (error: unknown) {
      console.error("Reset password error:", error);
      const errorMessage = error instanceof Error ? error.message : "Erro ao enviar email de recuperação. Tente novamente.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background flex items-center justify-center p-4 sm:p-6 lg:p-8 font-sans">
      <div className="w-full max-w-md lg:max-w-lg xl:max-w-xl mx-auto">
        <div className="text-center mb-6 sm:mb-8">
          <Card className="mb-4 sm:mb-6 shadow-lg">
            <CardContent className="pt-6 pb-4 sm:pb-6">
              <img src={partnersLogo} alt="Silva Partners" className="mx-auto h-16 sm:h-20 mb-4" />
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">Silva Partners</h1>
            </CardContent>
          </Card>
        </div>

        <Card className="w-full shadow-xl border-0">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl sm:text-3xl font-bold">
              {emailSent ? "Email Enviado!" : "Recuperar Senha"}
            </CardTitle>
            <CardDescription className="text-sm sm:text-base">
              {emailSent
                ? "Verifique sua caixa de entrada e siga as instruções para redefinir sua senha."
                : "Digite seu email para receber um link de recuperação"}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            {emailSent ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    Enviamos um email com instruções para redefinir sua senha. Verifique sua caixa de entrada e spam.
                  </p>
                </div>
                <Link to="/">
                  <Button variant="outline" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar ao Login
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      {...register("email", {
                        required: "Email é obrigatório",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Email inválido",
                        },
                      })}
                      type="email"
                      id="email"
                      className="pl-10 h-11 sm:h-12"
                      placeholder="seu@email.com"
                    />
                  </div>
                  {errors.email && <p className="text-xs sm:text-sm text-destructive">{errors.email.message}</p>}
                </div>

                <Button type="submit" disabled={isLoading} className="w-full h-11 sm:h-12 text-sm sm:text-base font-medium">
                  {isLoading ? "Enviando..." : "Enviar Link de Recuperação"}
                </Button>
              </form>
            )}

            <div className="mt-4 sm:mt-6 text-center">
              <Link to="/">
                <Button variant="link" className="p-0 h-auto font-medium text-primary hover:text-primary/80">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Voltar ao Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

